import {
  useCallback, useMemo, useRef, useState,
} from 'react'

import useIsomorphicLayoutEffect from 'Utils/useIsomorphicLayoutEffect'

// Sub-pixel widths can round a control that exactly fits into a collapse.
const FIT_TOLERANCE = 0.5

const isInFlow = node => window.getComputedStyle(node).position !== 'absolute'
const widthOf = node => node.getBoundingClientRect().width
const gapOf = node => parseFloat(window.getComputedStyle(node).columnGap) || 0
const sumWidths = nodes => nodes.reduce((sum, node) => sum + widthOf(node), 0)

// The controls sit in the composer's controls group, which shares the actions row
// with the send button. Collapsed controls are lifted out of flow (measure-only)
// but keep their box, so every control's width stays readable here whatever the
// current split is.
const countFitting = (controls) => {
  const group = controls[0].parentElement
  const row = group.parentElement
  const collapsible = new Set(controls)

  const rowItems = Array.from(row.children).filter(isInFlow)
  const rowFixed = sumWidths(rowItems.filter(node => node !== group)) + (rowItems.length - 1) * gapOf(row)

  const groupGap = gapOf(group)
  const groupFixed = Array.from(group.children).filter(node => !collapsible.has(node) && isInFlow(node))
  let used = rowFixed + sumWidths(groupFixed) + Math.max(groupFixed.length - 1, 0) * groupGap

  const available = widthOf(row) + FIT_TOLERANCE
  let count = 0
  for (let index = 0; index < controls.length; index += 1) {
    used += widthOf(controls[index]) + (groupFixed.length + index > 0 ? groupGap : 0)
    if (used > available) break
    count += 1
  }

  return count
}

/**
 * Tracks how many of the composer's collapsible controls fit its actions row. The
 * rest collapse from the end of the list, so the last control goes first.
 * @param {number} count - Number of collapsible controls, in row order
 * @returns {{ visibleCount: number, controlRefs: Function[] }} - How many leading
 * controls fit, and a callback ref for each control's wrapper
 */
function useCollapsedControls(count) {
  const nodesRef = useRef([])
  const [visibleCount, setVisibleCount] = useState(count)

  const controlRefs = useMemo(
    () => Array.from({ length: count }, (_, index) => (node) => { nodesRef.current[index] = node }),
    [count],
  )

  const recompute = useCallback(() => {
    const controls = nodesRef.current.slice(0, count)
    if (controls.length < count || controls.some(node => !node?.parentElement?.parentElement)) return

    setVisibleCount(countFitting(controls))
  }, [count])

  useIsomorphicLayoutEffect(() => {
    const controls = nodesRef.current.slice(0, count).filter(Boolean)
    if (controls.length === 0) return undefined

    recompute()

    if (typeof ResizeObserver === 'undefined') return undefined

    // The row resizes with the panel; its items resize on their own when a label
    // changes (another model picked, the upload label hidden on mobile, fonts landing).
    const group = controls[0].parentElement
    const row = group?.parentElement
    const observer = new ResizeObserver(recompute)
    if (row) {
      observer.observe(row)
      Array.from(row.children).forEach(node => observer.observe(node))
    }
    if (group) Array.from(group.children).forEach(node => observer.observe(node))

    return () => observer.disconnect()
  }, [count, recompute])

  return { visibleCount, controlRefs }
}

export default useCollapsedControls
